// UI strings for the pronunciation area, Finnish (default) and English side by side.
// Finnish is written in full and never truncated; en must have exactly the same shape.
// Every fact about High Valyrian here is from Peterson's wiki, Appendix:High Valyrian pronunciation
// (see `sources`); facts about Finnish are standard Finnish phonetics.

import { analysisEn, analysisFi } from './analysis';
import { defineArea } from '../area';

export interface PathPoint {
  h: string;
  p: string;
  /** HV example words (rendered with stress display). */
  words?: string[];
}

export const pronunciationFi = {
  title: 'Ääntäminen',
  lessonLink: 'Oppitunti 1: äänteet ja paino',
  steps: { yours: 'Mikä on jo sinun', fresh: 'Uudet äänteet', stress: 'Painon ansa' },
  yours: [
    {
      h: 'Pitkät ja lyhyet vokaalit',
      p: 'Vokaaleja on kuusi – a, e, i, o, u ja y – ja jokainen voi olla lyhyt tai pitkä. Pitkä vokaali kuulostaa samalta kuin lyhyt, vain pidempänä, kuten suomessa. Pituus merkitään viivalla vokaalin päällä (ā), ei kahdella kirjaimella (aa).',
      words: ['ābra', 'zaldrīzes'],
    },
    {
      h: 'y on suomen y',
      p: 'Wiki vertaa y:tä saksan ü:hön (tschüss) ja ȳ:tä pitkään üü:hön (Zürich). Se on sama pyöreä etuvokaali kuin suomen y ja yy.',
      words: ['rytsas', 'sȳz'],
    },
    { h: 'k, p ja t ilman henkäystä', p: 'Klusiilit k, p ja t äännetään aina ilman henkäystä – juuri niin kuin suomessa, ei niin kuin englannissa.' },
    { h: 'Täryllinen r', p: 'Klassinen r on täryäänne kuten rr espanjan sanassa perro, eli samanlainen kuin suomen r. Joskus se on lyhyt napautus.' },
    {
      h: 'Kaksoiskonsonantit',
      p: 'Kuten suomessa, konsonantit voivat olla pitkiä eli kahdennettuja: esimerkiksi tt, nn, ss, ll, rr, mm, dd ja kk. Pidä ne pitkinä, kuten sanassa kissa.',
      words: ['kessa', 'issa'],
    },
    { h: 'n ennen k:ta', p: 'n mukautuu seuraavaan konsonanttiin: ennen k:ta ja g:tä se on [ŋ], samoin kuin suomen sanassa kenkä.' },
  ] as PathPoint[],
  fresh: [
    {
      h: 'j ei ole suomen j',
      p: 'Klassisesti j on [ɟ], soinnillinen palataaliklusiili – wiki vertaa sitä englannin sanan eggyolk ggy-yhtymään. Myöhemmin sitä äännettiin myös [j], [ʒ] tai [dʒ]. Sanakirjan IPA käyttää klassista [ɟ]:tä.',
      words: ['jentys', 'jiōrna'],
    },
    {
      h: 'gh',
      p: 'gh on [ɣ], soinnillinen frikatiivi kuten g espanjan sanassa fuego. Joskus se äännetään kauempaa kurkusta [ʁ] kuten ranskan r.',
      words: ['morghūlis'],
    },
    { h: 'q', p: 'q on kuin k, mutta kauempana kurkussa: kielen takaosa koskettaa kitakielekettä [q].', words: ['qrīdrughās'] },
    { h: 'rh', p: 'rh on soinniton r.' },
    { h: 'ñ ja lj', p: 'ñ on [ɲ] kuten ni englannin sanassa onion, ja lj on [ʎ] kuten lli sanassa million.', words: ['ñuha'] },
    { h: 'z, v, b, d ja g', p: 'z on soinnillinen s (englannin zoo). Klassinen v on kuten v englannin sanassa voice. b, d ja g ovat soinnillisia.' },
    {
      h: 'Diftongit ae ja ao',
      p: 'Laskevat diftongit ae, ao, āe ja āo muodostavat yhden tavun. Suomessa a ja e kuuluvat tällaisessa kohdassa eri tavuihin (va-el-lus), valyriassa samaan: do·HAE·ris.',
      words: ['dohaeris', 'taoba'],
    },
    { h: 'Lainasanojen äänteet', p: 'f, kh, sh ja th esiintyvät vain lainasanoissa.' },
  ] as PathPoint[],
  stress: [
    { h: 'Suomessa paino on aina ensimmäisellä tavulla', p: 'Korkeavalyriassa ei. Suomalainen sanoo helposti ZAL-drī-zes tai DRA-ka-rys – molemmat väärin.' },
    {
      h: 'Sääntö',
      p: 'Tavallisesti paino on toiseksi viimeisellä tavulla. Kolmanneksi viimeinen tavu saa painon, jos se on raskas ja toiseksi viimeinen kevyt.',
      words: ['zaldrīzes', 'drakarys', 'kirimvose', 'qrīdrughās'],
    },
    {
      h: 'Raskas ja kevyt tavu',
      p: 'Tavu on kevyt vain, jos se päättyy lyhyeen vokaaliin. Raskas tavu sisältää pitkän vokaalin tai laskevan diftongin, tai se päättyy konsonanttiin. Sanan sisällä konsonantti aloittaa mieluummin seuraavan tavun (myös klusiili + l, r tai s), joten zal·drī, ei zald·rī.',
    },
    {
      h: 'Poikkeukset',
      p: 'Käskymuodoissa paino on viimeisellä tavulla. Rinnastuksen pidennetyssä muodossa paino pysyy alkuperäisen sanan viimeisellä tavulla. Lainasanat pitävät painon siinä, missä se oli lähtökielessä.',
    },
  ] as PathPoint[],
  practice: 'Harjoittele ääneen',
  words: 'Sanat',
  lines: 'Repliikit sarjasta',
  openPractice: 'Äänitä ja vertaa',
  closePractice: 'Piilota äänitys',
  inDictionary: 'Sanakirjassa',
  sources: 'Lähteet',
  sourceNote:
    'Korkeavalyriaa koskevat tiedot ovat Petersonin wikistä. Sovellus päättelee painon wikin sääntöjen mukaan, ja merkinnät vastaavat wikin IPA-merkintöjä.',
  rec: {
    title: 'Oma äänitys',
    hold: 'Pidä pohjassa ja puhu',
    holdHint: 'Päästä irti, kun olet valmis. Lyhyt napautus aloittaa ja toinen lopettaa.',
    recording: 'Äänitetään…',
    stop: 'Lopeta äänitys',
    maxNote: (s: number) => `Enintään ${s} sekuntia.`,
    yours: 'Sinun äänityksesi',
    peterson: 'Peterson',
    playYours: 'Kuuntele omasi',
    pauseYours: 'Tauko',
    compare: 'Peterson, sitten sinä',
    again: 'Äänitä uudelleen',
    discard: 'Hylkää',
    upload: 'Tallenna kumppanin kuultavaksi',
    uploading: 'Tallennetaan…',
    uploaded: 'Tallennettu. Kumppanisi voi kuunnella sen ja kirjoittaa siitä huomion.',
    uploadOffline: 'Tallentaminen vaatii yhteyden. Äänitys säilyy tässä, kunnes poistut sivulta.',
    uploadFailed: 'Tallentaminen ei onnistunut. Yritä uudelleen.',
    uploadDemo: 'Demotilassa äänityksiä ei tallenneta palvelimelle.',
    tooLarge: 'Äänitys on liian suuri (enintään 5 Mt).',
    denied: 'Mikrofonin käyttö on estetty. Salli se selaimen tai iPhonen asetuksista ja yritä uudelleen.',
    unsupported: 'Tämä selain ei osaa äänittää.',
    failed: 'Äänitys ei onnistunut.',
    noReference: 'Tästä ei ole Petersonin äänitettä, joten vertailkaa keskenänne.',
    saved: 'Tallennetut äänitykset',
    none: 'Ei vielä tallennettuja äänityksiä.',
    by: (name: string, date: string) => `${name} · ${date}`,
    play: 'Kuuntele',
    pause: 'Tauko',
    delete: 'Poista äänitys',
    deleted: 'Poistettu.',
    note: 'Huomio',
    noteBy: (name: string) => `${name}:`,
    addNote: 'Kirjoita huomio',
    notePlaceholder: 'esim. paino osui oikein, gh voisi olla pehmeämpi',
    noteSave: 'Tallenna huomio',
    noteHint: 'Huomion äänitykseen kirjoittaa kumppani, ei äänittäjä itse.',
    releaseToStop: 'Päästä irti, niin äänitys loppuu',
    starting: 'Avataan mikrofonia…',
    tapHint: 'Napauta uudelleen, kun olet valmis.',
    micReady: 'Mikrofoni on nyt käytössä, mutta tästä painalluksesta ei tallentunut mitään. Paina uudelleen.',
    deleteConfirm: 'Poistetaanko äänitys?',
    deleteYes: 'Poista',
    newTitle: 'Uudet äänitykset',
    newIntro: (name: string) => `${name} on äänittänyt nämä, eikä niistä ole vielä huomiota. Kuuntele ja kirjoita, mitä kuulit.`,
    newNone: 'Ei uusia äänityksiä kuunneltavana.',
  },
  /** Ääntämisanalyysi panel after a recording (long-vowel meter; the stress check adds its own). */
  analysis: analysisFi,
};

export const pronunciationEn: typeof pronunciationFi = {
  title: 'Pronunciation',
  lessonLink: 'Lesson 1: sounds and stress',
  steps: { yours: 'What is already yours', fresh: 'New sounds', stress: 'The stress trap' },
  yours: [
    {
      h: 'Long and short vowels',
      p: 'There are six vowels — a, e, i, o, u and y — and each can be short or long. A long vowel sounds like the short one, only longer, as in Finnish. Length is written with a macron (ā), not a double letter (aa).',
      words: ['ābra', 'zaldrīzes'],
    },
    {
      h: 'y is the Finnish y',
      p: 'The wiki compares y to German ü (tschüss) and ȳ to a long ü (Zürich). It is the same rounded front vowel as Finnish y and yy.',
      words: ['rytsas', 'sȳz'],
    },
    { h: 'k, p and t without aspiration', p: 'The stops k, p and t are never aspirated — just as in Finnish, not as in English.' },
    { h: 'A trilled r', p: 'The classical r is trilled like the rr in Spanish perro, much like the Finnish r. Sometimes it is a short tap.' },
    {
      h: 'Double consonants',
      p: 'There are long (geminate) consonants, as in Finnish: for example tt, nn, ss, ll, rr, mm, dd and kk. Keep them long, as in kissa.',
      words: ['kessa', 'issa'],
    },
    { h: 'n before k', p: 'n takes on the place of the next consonant: before k and g it is [ŋ], as in Finnish kenkä.' },
  ],
  fresh: [
    {
      h: 'j is not the Finnish j',
      p: 'Classically j is [ɟ], a voiced palatal stop — in the wiki’s comparison like the ggy in English “eggyolk”. Later it was also pronounced [j], [ʒ] or [dʒ]. The dictionary’s IPA uses the classical [ɟ].',
      words: ['jentys', 'jiōrna'],
    },
    { h: 'gh', p: 'gh is [ɣ], a voiced fricative like the g in Spanish fuego; sometimes further back, [ʁ], like the French r.', words: ['morghūlis'] },
    { h: 'q', p: 'q is like k, but further back in the throat: the back of the tongue touches the uvula [q].', words: ['qrīdrughās'] },
    { h: 'rh', p: 'rh is a voiceless r.' },
    { h: 'ñ and lj', p: 'ñ is [ɲ] like the ni in English onion, and lj is [ʎ] like the lli in million.', words: ['ñuha'] },
    { h: 'z, v, b, d and g', p: 'z is a voiced s (English zoo). The classical v is like English voice. b, d and g are voiced.' },
    {
      h: 'The diphthongs ae and ao',
      p: 'The falling diphthongs ae, ao, āe and āo are one syllable. In Finnish, a and e in such a position fall into separate syllables (va-el-lus); in Valyrian into one: do·HAE·ris.',
      words: ['dohaeris', 'taoba'],
    },
    { h: 'Sounds of loanwords', p: 'f, kh, sh and th appear only in loanwords.' },
  ],
  stress: [
    {
      h: 'In Finnish the stress is always on the first syllable',
      p: 'Not in High Valyrian. A Finnish ear easily says ZAL-drī-zes or DRA-ka-rys — both wrong.',
    },
    {
      h: 'The rule',
      p: 'Usually the second-to-last syllable is stressed. The third-to-last takes the stress if it is heavy and the second-to-last is light.',
      words: ['zaldrīzes', 'drakarys', 'kirimvose', 'qrīdrughās'],
    },
    {
      h: 'Heavy and light syllables',
      p: 'A syllable is light only if it ends in a short vowel. A heavy syllable has a long vowel or a falling diphthong, or ends in a consonant. Inside a word a consonant prefers to start the next syllable (so does a stop + l, r or s), so zal·drī, not zald·rī.',
    },
    {
      h: 'Exceptions',
      p: 'Imperative verb forms stress the last syllable. In the lengthened form of simple coordination the stress stays on the original word’s last syllable. Loanwords keep the stress where it was in the source language.',
    },
  ],
  practice: 'Practise out loud',
  words: 'Words',
  lines: 'Lines from the show',
  openPractice: 'Record and compare',
  closePractice: 'Hide recording',
  inDictionary: 'In the dictionary',
  sources: 'Sources',
  sourceNote: 'Facts about High Valyrian from Peterson’s wiki. The app works out the stress from the wiki’s rules, and the marks agree with the wiki’s IPA.',
  rec: {
    title: 'Your recording',
    hold: 'Hold and speak',
    holdHint: 'Let go when you are done. A short tap starts, a second tap stops.',
    recording: 'Recording…',
    stop: 'Stop recording',
    maxNote: (s) => `Up to ${s} seconds.`,
    yours: 'Your recording',
    peterson: 'Peterson',
    playYours: 'Play yours',
    pauseYours: 'Pause',
    compare: 'Peterson, then you',
    again: 'Record again',
    discard: 'Discard',
    upload: 'Save for your partner to hear',
    uploading: 'Saving…',
    uploaded: 'Saved. Your partner can listen and leave a note.',
    uploadOffline: 'Saving needs a connection. The recording stays here until you leave the page.',
    uploadFailed: 'Saving failed. Try again.',
    uploadDemo: 'In demo mode recordings are not saved to the server.',
    tooLarge: 'The recording is too large (5 MB at most).',
    denied: 'Microphone access is blocked. Allow it in the browser or iPhone settings and try again.',
    unsupported: 'This browser cannot record audio.',
    failed: 'Recording failed.',
    noReference: 'There is no recording by Peterson for this, so compare with your partner.',
    saved: 'Saved recordings',
    none: 'No saved recordings yet.',
    by: (name, date) => `${name} · ${date}`,
    play: 'Play',
    pause: 'Pause',
    delete: 'Delete recording',
    deleted: 'Deleted.',
    note: 'Note',
    noteBy: (name) => `${name}:`,
    addNote: 'Write a note',
    notePlaceholder: 'e.g. the stress was right, gh could be softer',
    noteSave: 'Save the note',
    noteHint: 'Only the partner writes a note on someone’s recording.',
    releaseToStop: 'Let go to stop',
    starting: 'Opening the microphone…',
    tapHint: 'Tap again when you are done.',
    micReady: 'The microphone is on now, but nothing was recorded from that press. Press again.',
    deleteConfirm: 'Delete this recording?',
    deleteYes: 'Delete',
    newTitle: 'New recordings',
    newIntro: (name) => `${name} recorded these and there is no note on them yet. Listen and write what you heard.`,
    newNone: 'No new recordings to listen to.',
  },
  analysis: analysisEn,
};

// Messages.pronunciation (i18n/area.ts: collected by area-registry.ts)
export const area = defineArea('pronunciation', pronunciationFi, pronunciationEn);
declare module '../area' {
  interface AreaMessages extends Record<typeof area.key, typeof pronunciationFi> {}
}
